import { useState } from 'react';
import { Box, Text, Stack, UnstyledButton, Alert, Title, Code } from '@mantine/core';
import {
  IconSearch, IconChartBar, IconFiles, IconSettings, IconAlertCircle,
} from '@tabler/icons-react';

import IvrSearch from './IvrSearch';
import IvrStats from './IvrStats';
import IvrBrowser from './IvrBrowser';
import IvrAdmin from './IvrAdmin';
import { isIvrBackendConfigured } from './api_scripts/ivrClient';
import Footer from '../../components/Footer';
import PlatformBackButton from '../../components/PlatformBackButton';

const NAV = [
  { key: 'search',    label: 'Search',    icon: IconSearch },
  { key: 'stats',     label: 'Stats',     icon: IconChartBar },
  { key: 'documents', label: 'Documents', icon: IconFiles },
  { key: 'admin',     label: 'Admin',     icon: IconSettings },
];

const SIDEBAR_W = 220;
const INDIGO = '#4263eb';
const INDIGO_BG = '#edf2ff';

function IvrApp({ onBackToPlatformSelector }) {
  const [active, setActive] = useState('search');
  const apiConfigured = isIvrBackendConfigured();

  const content = {
    search:    <IvrSearch />,
    stats:     <IvrStats />,
    documents: <IvrBrowser />,
    admin:     <IvrAdmin />,
  }[active];

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

      {!apiConfigured && (
        <Alert
          color="red"
          icon={<IconAlertCircle size={16} />}
          title="IVR API URL not configured"
          radius={0}
        >
          Set <Code>REACT_APP_IVR_API_BASE_URL</Code> environment variable. All IVR
          API calls will fail until this is configured.
        </Alert>
      )}

      {/* Indigo gradient header */}
      <Box style={{
        background: 'linear-gradient(135deg, #4263eb 0%, #748ffc 100%)',
        padding: '18px 28px',
      }}>
        <Title order={2} c="white" style={{ lineHeight: 1.2 }}>
          Conv. AI Use Case Library
        </Title>
        <Text size="sm" style={{ color: 'rgba(255,255,255,0.85)', marginTop: 4 }}>
          IVR use cases extracted from SharePoint project documents
        </Text>
      </Box>

      {/* Sidebar + main content row */}
      <Box style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar */}
        <Box style={{
          width: SIDEBAR_W,
          minWidth: SIDEBAR_W,
          background: '#ffffff',
          borderRight: '1px solid #e9ecef',
          display: 'flex',
          flexDirection: 'column',
          padding: '20px 0 16px',
        }}>

          <Box style={{ padding: '0 16px 16px' }}>
            <PlatformBackButton
              onClick={onBackToPlatformSelector}
              color="indigo"
              mb="md"
            />
            <Text fw={700} size="sm" style={{ color: INDIGO, lineHeight: 1.2 }}>
              Total Experience
            </Text>
            <Text size="xs" c="dimmed" style={{ lineHeight: 1.3, marginTop: 2 }}>
              Conversational AI Use Case Library
            </Text>
          </Box>

          <Stack gap={2} style={{ flex: 1, padding: '0 8px' }}>
            {NAV.map(({ key, label, icon: Icon }) => {
              const isActive = active === key;
              return (
                <UnstyledButton
                  key={key}
                  onClick={() => setActive(key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '9px 12px',
                    borderRadius: 8,
                    background: isActive ? INDIGO_BG : 'transparent',
                    color: isActive ? INDIGO : '#495057',
                    fontWeight: isActive ? 600 : 400,
                    fontSize: 14,
                    width: '100%',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f1f3f5'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <Icon size={16} style={{ flexShrink: 0 }} />
                  {label}
                </UnstyledButton>
              );
            })}
          </Stack>
        </Box>

        {/* Main content */}
        <Box style={{
          flex: 1,
          background: '#f8f9fa',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {content}
        </Box>
      </Box>

      <Footer platformColor="indigo" />
    </Box>
  );
}

export default IvrApp;
